import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import SimpleTextEditor from '../components/SimpleTextEditor';
import { uploadAwardImage } from '../lib/storage';
import type { MediaMention } from '../contexts/LanguageContext';

interface Award {
  id?: string;
  slug: string;
  language: string;
  title: string;
  description: string;
  image_url: string;
  year: string;
  sort_order: number;
}

interface Article {
  slug: string;
  title: string;
  language: string;
  date: string;
  description?: string;
  content?: string;
  created_at?: string;
  published?: boolean;
}

const API_URL = '/api/admin-articles';
const AWARDS_API_URL = '/api/admin-awards';

const WorkingAdmin: React.FC = () => {
  const [view, setView] = useState<'login' | 'list' | 'create' | 'edit' | 'awards' | 'award-create' | 'award-edit'>('login');
  const [activeTab, setActiveTab] = useState<'articles' | 'awards' | 'media'>('articles');
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem('adminAuth') === 'true');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editingBgSlug, setEditingBgSlug] = useState<string | null>(null);
  const [editingEnSlug, setEditingEnSlug] = useState<string | null>(null);
  const [editingPublished, setEditingPublished] = useState(true);
  const [publishingPair, setPublishingPair] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [awards, setAwards] = useState<Award[]>([]);
  const [awardDeleteConfirm, setAwardDeleteConfirm] = useState<string | null>(null);
  const [editingAwardSlug, setEditingAwardSlug] = useState<string | null>(null);

  // Award form state
  const [bgAwardTitle, setBgAwardTitle] = useState('');
  const [bgAwardDesc, setBgAwardDesc] = useState('');
  const [enAwardTitle, setEnAwardTitle] = useState('');
  const [enAwardDesc, setEnAwardDesc] = useState('');
  const [awardImageUrl, setAwardImageUrl] = useState('');
  const [awardYear, setAwardYear] = useState('');
  const [awardSortOrder, setAwardSortOrder] = useState(0);
  const [awardSlugBase, setAwardSlugBase] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const awardImageInputRef = useRef<HTMLInputElement>(null);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimeRemaining, setLockTimeRemaining] = useState(0);

  const MAX_LOGIN_ATTEMPTS = 5;
  const LOCK_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds

  React.useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (isAuthenticated && savedPassword) {
      setAdminPassword(savedPassword);
      fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list', password: savedPassword }) })
        .then(r => r.ok ? r.json() : null)
        .then(json => { if (json) setArticles(json.articles || []); });
      fetch(AWARDS_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list', password: savedPassword }) })
        .then(r => r.ok ? r.json() : null)
        .then(json => { if (json) setAwards(json.awards || []); });
    }
  }, [isAuthenticated]);

  // Push a history entry when entering any edit/create view so the browser back button returns to list
  React.useEffect(() => {
    const editViews = ['edit', 'create', 'award-edit', 'award-create'];
    if (editViews.includes(view)) {
      window.history.pushState({ adminView: view }, '');
    }
  }, [view]);

  // Intercept browser back button — go to list instead of leaving the page
  React.useEffect(() => {
    const handlePopState = () => {
      setView('list');
      resetForm();
      resetAwardForm();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const awardsApiCall = async (action: string, data?: any) => {
    const res = await fetch(AWARDS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, password: adminPassword, data }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  };

  const apiCall = async (action: string, data?: any) => {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, password: adminPassword, data }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  };

  // Form state
  const [bgTitle, setBgTitle] = useState('');
  const [bgDescription, setBgDescription] = useState('');
  const [bgContent, setBgContent] = useState('');
  const [enTitle, setEnTitle] = useState('');
  const [enDescription, setEnDescription] = useState('');
  const [enContent, setEnContent] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [slugBase, setSlugBase] = useState('');

  const fetchAwards = async () => {
    try {
      const result = await awardsApiCall('list');
      setAwards(result.awards || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load awards');
    }
  };

  const resetAwardForm = () => {
    setBgAwardTitle('');
    setBgAwardDesc('');
    setEnAwardTitle('');
    setEnAwardDesc('');
    setAwardImageUrl('');
    setAwardYear('');
    setAwardSortOrder(0);
    setAwardSlugBase('');
    setEditingAwardSlug(null);
    if (awardImageInputRef.current) awardImageInputRef.current.value = '';
  };

  const handleAwardImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);
    setError('');
    try {
      const base = awardSlugBase || generateSlug(bgAwardTitle) || `award-${Date.now()}`;
      const url = await uploadAwardImage(file, base);
      setAwardImageUrl(url);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAwardCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await awardsApiCall('create', {
        awards: [
          { slug: `${awardSlugBase || generateSlug(bgAwardTitle)}-bg`, language: 'bg', title: bgAwardTitle, description: bgAwardDesc, image_url: awardImageUrl, year: awardYear, sort_order: awardSortOrder, published: true },
          { slug: `${awardSlugBase || generateSlug(bgAwardTitle)}-en`, language: 'en', title: enAwardTitle, description: enAwardDesc, image_url: awardImageUrl, year: awardYear, sort_order: awardSortOrder, published: true },
        ],
      });
      setSuccess('Award created successfully!');
      resetAwardForm();
      setTimeout(() => { setView('list'); setActiveTab('awards'); fetchAwards(); setSuccess(''); }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to save award');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAwardEdit = (bg: Award, en: Award) => {
    const slugBase = bg.slug.replace(/-bg$/, '');
    setEditingAwardSlug(slugBase);
    setBgAwardTitle(bg.title || '');
    setBgAwardDesc(bg.description || '');
    setEnAwardTitle(en.title || '');
    setEnAwardDesc(en.description || '');
    setAwardImageUrl(bg.image_url || '');
    setAwardYear(bg.year || '');
    setAwardSortOrder(bg.sort_order || 0);
    setAwardSlugBase(slugBase);
    setView('award-edit');
  };

  const handleAwardUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAwardSlug) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await awardsApiCall('update', {
        awards: [
          { slug: `${editingAwardSlug}-bg`, language: 'bg', title: bgAwardTitle, description: bgAwardDesc, image_url: awardImageUrl, year: awardYear, sort_order: awardSortOrder, published: true },
          { slug: `${editingAwardSlug}-en`, language: 'en', title: enAwardTitle, description: enAwardDesc, image_url: awardImageUrl, year: awardYear, sort_order: awardSortOrder, published: true },
        ],
      });
      setSuccess('Award updated successfully!');
      setTimeout(() => { setView('list'); setActiveTab('awards'); fetchAwards(); setSuccess(''); setEditingAwardSlug(null); }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update award');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmAwardDelete = async () => {
    if (!awardDeleteConfirm) return;
    try {
      await awardsApiCall('delete', { slug: awardDeleteConfirm });
      fetchAwards();
    } catch (err: any) {
      setError(err.message || 'Failed to delete award');
    } finally {
      setAwardDeleteConfirm(null);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLocked) {
      setError(`Account locked. Try again in ${Math.ceil(lockTimeRemaining / 60000)} minutes.`);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', password }),
      });

      if (res.ok) {
        const json = await res.json();
        setAdminPassword(password);
        sessionStorage.setItem('adminAuth', 'true');
        sessionStorage.setItem('adminPassword', password);
        setIsAuthenticated(true);
        setError('');
        setLoginAttempts(0);
        setArticles(json.articles || []);
        const awardsResult = await fetch(AWARDS_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list', password }) });
        if (awardsResult.ok) { const awardsJson = await awardsResult.json(); setAwards(awardsJson.awards || []); }
      } else {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
          setIsLocked(true);
          setLockTimeRemaining(LOCK_TIME);
          
          const countdown = setInterval(() => {
            setLockTimeRemaining(prev => {
              if (prev <= 1000) {
                clearInterval(countdown);
                setIsLocked(false);
                setLoginAttempts(0);
                return 0;
              }
              return prev - 1000;
            });
          }, 1000);
          
          setError('Too many failed attempts. Account locked for 15 minutes.');
        } else {
          setError(`Invalid password. ${MAX_LOGIN_ATTEMPTS - newAttempts} attempts remaining.`);
        }
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
      setPassword('');
    }
  };

  const fetchArticles = async () => {
    setIsLoading(true);
    try {
      const result = await apiCall('list');
      setArticles(result.articles || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load articles');
    } finally {
      setIsLoading(false);
    }
  };

  const sanitizeSlug = (input: string): string => {
    return input.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
  };

  const finalizeSlug = (input: string): string => {
    return input.replace(/^-|-$/g, '');
  };

  const generateSlug = (input: string): string => {
    const cyrillic: Record<string, string> = {
      а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ж:'zh',з:'z',и:'i',й:'y',к:'k',л:'l',м:'m',
      н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',х:'h',ц:'ts',ч:'ch',ш:'sh',
      щ:'sht',ъ:'a',ь:'',ю:'yu',я:'ya',
    };
    return input.toLowerCase()
      .split('').map(c => cyrillic[c] ?? c).join('')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const parseDateToISO = (localizedDate: string): string => {
    const bgMonths = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
    const enMonths = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    // Try BG format: "5 април 2026"
    const bgMatch = localizedDate.match(/^(\d{1,2})\s+(\S+)\s+(\d{4})$/);
    if (bgMatch) {
      const day = bgMatch[1].padStart(2, '0');
      const monthIdx = bgMonths.indexOf(bgMatch[2].toLowerCase());
      if (monthIdx !== -1) return `${bgMatch[3]}-${String(monthIdx + 1).padStart(2, '0')}-${day}`;
    }
    // Try EN format: "April 5, 2026"
    const enMatch = localizedDate.match(/^(\S+)\s+(\d{1,2}),?\s+(\d{4})$/);
    if (enMatch) {
      const monthIdx = enMonths.indexOf(enMatch[1].toLowerCase());
      const day = enMatch[2].padStart(2, '0');
      if (monthIdx !== -1) return `${enMatch[3]}-${String(monthIdx + 1).padStart(2, '0')}-${day}`;
    }
    // Already ISO or unrecognized — return as-is
    return localizedDate;
  };

  const formatDate = (isoDate: string, lang: string): string => {
    const bgMonths = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
    const enMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const parts = isoDate.split('-');
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);

    if (lang === 'bg') {
      return `${day} ${bgMonths[monthIndex]} ${year}`;
    }
    return `${enMonths[monthIndex]} ${day}, ${year}`;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const bgSlug = slugBase ? `blog/${slugBase}-bg` : `blog/${generateSlug(bgTitle)}`;
      const enSlug = slugBase ? `blog/${slugBase}-en` : `blog/${generateSlug(enTitle)}`;

      const bgArticle = {
        date: formatDate(date, 'bg'),
        slug: bgSlug,
        language: 'bg',
        title: bgTitle,
        description: bgDescription,
        content: bgContent,
        published: true,
      };

      const enArticle = {
        date: formatDate(date, 'en'),
        slug: enSlug,
        language: 'en',
        title: enTitle,
        description: enDescription,
        content: enContent,
        published: true,
      };

      await apiCall('create', { articles: [bgArticle, enArticle] });

      setSuccess('Article published successfully!');
      resetForm();
      setTimeout(() => {
        setView('list');
        fetchArticles();
        setSuccess('');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to save article');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (clicked: Article) => {
    const pair = articles.filter(a => a.created_at === clicked.created_at);
    const bgArticle = pair.find(a => a.language === 'bg');
    const enArticle = pair.find(a => a.language === 'en');

    if (bgArticle && enArticle) {
      setEditingSlug(clicked.slug);
      setEditingBgSlug(bgArticle.slug);
      setEditingEnSlug(enArticle.slug);
      setBgTitle(bgArticle.title || '');
      setBgDescription(bgArticle.description || '');
      setBgContent(bgArticle.content || '');
      setEnTitle(enArticle.title || '');
      setEnDescription(enArticle.description || '');
      setEnContent(enArticle.content || '');
      setEditingPublished(bgArticle.published !== false && enArticle.published !== false);
      setDate(parseDateToISO(bgArticle.date || '') || new Date().toISOString().split('T')[0]);
      setSlugBase('');
      setView('edit');
    } else {
      setError('Could not find both language versions of this article');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSlug) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const bgSlug = editingBgSlug!;
      const enSlug = editingEnSlug!;

      const bgArticle = {
        date: formatDate(date, 'bg'),
        slug: bgSlug,
        language: 'bg',
        title: bgTitle,
        description: bgDescription,
        content: bgContent,
        published: editingPublished,
      };

      const enArticle = {
        date: formatDate(date, 'en'),
        slug: enSlug,
        language: 'en',
        title: enTitle,
        description: enDescription,
        content: enContent,
        published: editingPublished,
      };

      await apiCall('update', { articles: [bgArticle, enArticle] });

      setSuccess('Article updated successfully!');
      setTimeout(() => {
        setView('list');
        fetchArticles();
        setSuccess('');
        setEditingSlug(null);
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update article');
    } finally {
      setIsSaving(false);
    }
  };

  const setPairPublished = async (article: Article, published: boolean) => {
    if (!article.created_at) {
      setError('Article pair is missing its creation timestamp.');
      return;
    }

    setPublishingPair(article.created_at);
    setError('');
    setSuccess('');
    try {
      await apiCall('set-published', { created_at: article.created_at, published });
      setArticles(current => current.map(item => item.created_at === article.created_at ? { ...item, published } : item));
      setSuccess(published ? 'Both language versions are now published.' : 'Both language versions are now unpublished.');
    } catch (err: any) {
      setError(err.message || 'Article status was not changed.');
    } finally {
      setPublishingPair(null);
    }
  };

  const handleDelete = (slug: string) => {
    setDeleteConfirm(slug);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await apiCall('delete', { slug: deleteConfirm });
      fetchArticles();
    } catch (err: any) {
      setError(err.message || 'Failed to delete article');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setBgTitle('');
    setBgDescription('');
    setBgContent('');
    setEnTitle('');
    setEnDescription('');
    setEnContent('');
    setDate(new Date().toISOString().split('T')[0]);
    setSlugBase('');
    setEditingPublished(true);
  };

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e3a8a' }}>
        <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', width: '100%', maxWidth: '400px' }}>
          <h1 style={{ fontSize: '24px', marginBottom: '20px', color: '#1e3a8a' }}>Admin Login</h1>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '6px' }}
                placeholder="Enter password"
                required
                disabled={isLocked}
              />
            </div>
            {error && <p style={{ color: 'red', marginBottom: '16px' }}>{error}</p>}
            <button
              type="submit"
              style={{ width: '100%', padding: '12px', backgroundColor: '#1e3a8a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'award-create' || view === 'award-edit') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <h1 style={{ fontSize: '28px', color: '#1e3a8a' }}>{view === 'award-create' ? 'Add Award' : 'Edit Award'}</h1>
            <button onClick={() => { setView('list'); resetAwardForm(); }} style={{ padding: '8px 16px', backgroundColor: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
          </div>
          {error && <div style={{ padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '20px', color: '#c33' }}>{error}</div>}
          {success && <div style={{ padding: '12px', backgroundColor: '#efe', border: '1px solid #cfc', borderRadius: '4px', marginBottom: '20px', color: '#3c3' }}>{success}</div>}
          <form onSubmit={view === 'award-create' ? handleAwardCreate : handleAwardUpdate}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginBottom: '20px' }}>Settings</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Image</label>
                  <input
                    ref={awardImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAwardImageUpload}
                    style={{ display: 'none' }}
                    id="award-image-upload"
                  />
                  <label
                    htmlFor="award-image-upload"
                    style={{ display: 'inline-block', padding: '8px 16px', backgroundColor: isUploadingImage ? '#ccc' : '#1e3a8a', color: 'white', borderRadius: '4px', cursor: isUploadingImage ? 'not-allowed' : 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}
                  >
                    {isUploadingImage ? 'Uploading...' : '📁 Upload Image'}
                  </label>
                  {awardImageUrl && <p style={{ fontSize: '11px', color: '#2a2', marginTop: '6px', wordBreak: 'break-all' }}>✓ Uploaded</p>}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Year</label>
                  <input type="text" value={awardYear} onChange={(e) => setAwardYear(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} placeholder="2017" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Sort Order</label>
                  <input type="number" value={awardSortOrder} onChange={(e) => setAwardSortOrder(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
              </div>
              {awardImageUrl && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Image Preview</label>
                  <img src={awardImageUrl} alt="preview" style={{ maxHeight: '160px', borderRadius: '8px', border: '1px solid #eee' }} />
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginBottom: '20px', color: '#1e3a8a' }}>Bulgarian (BG)</h2>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Title *</label>
                  <input type="text" value={bgAwardTitle} onChange={(e) => setBgAwardTitle(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Description</label>
                  <textarea value={bgAwardDesc} onChange={(e) => setBgAwardDesc(e.target.value)} rows={4} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginBottom: '20px', color: '#b8860b' }}>English (EN)</h2>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Title *</label>
                  <input type="text" value={enAwardTitle} onChange={(e) => setEnAwardTitle(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Description</label>
                  <textarea value={enAwardDesc} onChange={(e) => setEnAwardDesc(e.target.value)} rows={4} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button type="button" onClick={() => { setView('list'); resetAwardForm(); }} style={{ padding: '12px 24px', backgroundColor: '#ccc', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button type="submit" disabled={isSaving} style={{ padding: '12px 24px', backgroundColor: '#d4af37', color: '#1e3a8a', border: 'none', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {isSaving ? 'Saving...' : view === 'award-create' ? 'Publish Award' : 'Update Award'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'create' || view === 'edit') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <h1 style={{ fontSize: '32px', color: '#1e3a8a' }}>{view === 'create' ? 'Create Article' : 'Edit Article'}</h1>
            <button
              onClick={() => { setView('list'); resetForm(); }}
              style={{ padding: '8px 16px', backgroundColor: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              ← Back to List
            </button>
          </div>

          {error && <div style={{ padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '20px', color: '#c33' }}>{error}</div>}
          {success && <div style={{ padding: '12px', backgroundColor: '#efe', border: '1px solid #cfc', borderRadius: '4px', marginBottom: '20px', color: '#3c3' }}>{success}</div>}

          <form onSubmit={view === 'create' ? handleCreate : handleUpdate}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginBottom: '20px' }}>Settings</h2>
              <div style={{ maxWidth: '200px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  required
                />
              </div>
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>URL Slug</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#999', fontSize: '13px' }}>blog/</span>
                  <input
                    type="text"
                    value={slugBase}
                    onChange={(e) => setSlugBase(sanitizeSlug(e.target.value))}
                    onBlur={(e) => setSlugBase(finalizeSlug(e.target.value))}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    placeholder="auto-generated-from-title"
                  />
                  <span style={{ fontSize: '12px', color: '#999' }}>{slugBase ? '-bg / -en' : '(auto)'}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginBottom: '20px', color: '#1e3a8a' }}>Bulgarian (BG)</h2>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Title *</label>
                  <input
                    type="text"
                    value={bgTitle}
                    onChange={(e) => setBgTitle(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    required
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Description</label>
                  <textarea
                    value={bgDescription}
                    onChange={(e) => setBgDescription(e.target.value)}
                    rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Content *</label>
                  <SimpleTextEditor
                    value={bgContent}
                    onChange={setBgContent}
                    placeholder="Съдържание на статията..."
                    editorId="editor-bg"
                  />
                </div>
              </div>

              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginBottom: '20px', color: '#1e3a8a' }}>English (EN)</h2>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Title *</label>
                  <input
                    type="text"
                    value={enTitle}
                    onChange={(e) => setEnTitle(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    required
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Description</label>
                  <textarea
                    value={enDescription}
                    onChange={(e) => setEnDescription(e.target.value)}
                    rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Content *</label>
                  <SimpleTextEditor
                    value={enContent}
                    onChange={setEnContent}
                    placeholder="Article content..."
                    editorId="editor-en"
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={() => { setView('list'); resetForm(); }}
                style={{ padding: '12px 24px', backgroundColor: '#ccc', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                style={{ padding: '12px 24px', backgroundColor: '#1e3a8a', color: 'white', border: 'none', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer' }}
              >
                {isSaving ? 'Saving...' : view === 'create' ? 'Publish Article' : 'Update Article'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#1e3a8a', padding: '20px 40px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '24px', color: 'white', margin: 0, fontWeight: 600 }}>Admin Panel</h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: '4px 0 0 0', fontSize: '14px' }}>
              {activeTab === 'articles' ? `${articles.length} article${articles.length !== 1 ? 's' : ''}` : activeTab === 'awards' ? `${awards.filter(a => a.language === 'bg').length} award${awards.filter(a => a.language === 'bg').length !== 1 ? 's' : ''}` : 'Publisher appearances'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {activeTab !== 'media' && <button
              onClick={() => { if (activeTab === 'articles') { setView('create'); resetForm(); } else { resetAwardForm(); const maxOrder = awards.reduce((m, a) => Math.max(m, a.sort_order ?? 0), 0); setAwardSortOrder(maxOrder + 1); setView('award-create'); } }}
              style={{ padding: '10px 20px', backgroundColor: '#d4af37', color: '#1e3a8a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
            >
              {activeTab === 'articles' ? '+ New Article' : '+ New Award'}
            </button>}
            <button
              onClick={() => { sessionStorage.removeItem('adminAuth'); sessionStorage.removeItem('adminPassword'); setIsAuthenticated(false); }}
              style={{ padding: '10px 16px', backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
            >
              Logout
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div style={{ maxWidth: '1200px', margin: '16px auto 0', display: 'flex', gap: '4px' }}>
          <button
            onClick={() => setActiveTab('articles')}
            style={{ padding: '8px 20px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, backgroundColor: activeTab === 'articles' ? 'white' : 'rgba(255,255,255,0.15)', color: activeTab === 'articles' ? '#1e3a8a' : 'white' }}
          >
            📝 Articles
          </button>
          <button
            onClick={() => setActiveTab('awards')}
            style={{ padding: '8px 20px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, backgroundColor: activeTab === 'awards' ? 'white' : 'rgba(255,255,255,0.15)', color: activeTab === 'awards' ? '#1e3a8a' : 'white' }}
          >
            🏆 Awards
          </button>
          <button
            onClick={() => setActiveTab('media')}
            style={{ padding: '8px 20px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, backgroundColor: activeTab === 'media' ? 'white' : 'rgba(255,255,255,0.15)', color: activeTab === 'media' ? '#1e3a8a' : 'white' }}
          >
            Media Mentions
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 40px' }}>
        {error && <div style={{ padding: '14px 18px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '20px', color: '#c33', fontSize: '14px' }}>{error}</div>}
        {success && <div style={{ padding: '14px 18px', backgroundColor: '#efe', border: '1px solid #cfc', borderRadius: '8px', marginBottom: '20px', color: '#276749', fontSize: '14px' }}>{success}</div>}

        {activeTab === 'media' ? <MediaMentionsAdmin apiCall={apiCall} articles={articles} /> : activeTab === 'awards' ? (
          isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>Loading awards...</div>
          ) : awards.filter(a => a.language === 'bg').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>No awards yet</p>
              <button onClick={() => { resetAwardForm(); const maxOrder = awards.reduce((m, a) => Math.max(m, a.sort_order ?? 0), 0); setAwardSortOrder(maxOrder + 1); setView('award-create'); }} style={{ padding: '12px 24px', backgroundColor: '#d4af37', color: '#1e3a8a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Add your first award</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {awards.filter(a => a.language === 'bg').map((award) => {
                const base = award.slug.replace(/-bg$/, '');
                return (
                  <div key={award.slug} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: '4px solid #d4af37' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: 1 }}>
                      {award.image_url && <img src={award.image_url} alt={award.title} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />}
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#1a1a2e', fontWeight: 600 }}>{award.title}</h3>
                        <span style={{ fontSize: '13px', color: '#888' }}>{award.year} · Sort: {award.sort_order}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '20px' }}>
                      <button onClick={() => { const en = awards.find(a => a.slug === `${base}-en`); if (en) handleAwardEdit(award, en); }} style={{ padding: '8px 16px', backgroundColor: 'white', color: '#1e3a8a', border: '1.5px solid #1e3a8a', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>Edit</button>
                      <button onClick={() => setAwardDeleteConfirm(base)} style={{ padding: '8px 16px', backgroundColor: 'white', color: '#c33', border: '1.5px solid #c33', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>Loading articles...</div>
        ) : articles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>No articles yet</p>
            <button
              onClick={() => { setView('create'); resetForm(); }}
              style={{ padding: '12px 24px', backgroundColor: '#1e3a8a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
            >
              Create your first article
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {articles.map((article) => (
              <div
                key={article.slug}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '20px 24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'box-shadow 0.2s',
                  borderLeft: article.language === 'bg' ? '4px solid #1e3a8a' : '4px solid #d4af37',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#1a1a2e', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {article.title}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      backgroundColor: article.language === 'bg' ? '#e8edf5' : '#fdf6e3',
                      color: article.language === 'bg' ? '#1e3a8a' : '#b8860b',
                      flexShrink: 0,
                    }}>
                      {article.language ? article.language.toUpperCase() : ''}
                    </span>
                    <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, backgroundColor: article.published === false ? '#fef3c7' : '#dcfce7', color: article.published === false ? '#92400e' : '#166534', flexShrink: 0 }}>
                      {article.published === false ? 'DRAFT' : 'PUBLISHED'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#888' }}>
                    <span>{article.date}</span>
                    <span style={{ color: '#bbb' }}>|</span>
                    <span style={{ color: '#aaa', fontFamily: 'monospace', fontSize: '12px' }}>/blog/{article.slug}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '20px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setPairPublished(article, article.published === false)}
                    disabled={publishingPair === article.created_at}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: article.published === false ? '#166534' : 'white',
                      color: article.published === false ? 'white' : '#92400e',
                      border: article.published === false ? '1.5px solid #166534' : '1.5px solid #d97706',
                      borderRadius: '6px',
                      cursor: publishingPair === article.created_at ? 'wait' : 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      opacity: publishingPair === article.created_at ? 0.6 : 1,
                    }}
                  >
                    {publishingPair === article.created_at ? 'Saving...' : article.published === false ? 'Publish both' : 'Unpublish both'}
                  </button>
                  <button
                    onClick={() => handleEdit(article)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'white',
                      color: '#1e3a8a',
                      border: '1.5px solid #1e3a8a',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(article.slug)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'white',
                      color: '#c33',
                      border: '1.5px solid #c33',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Award delete confirmation modal */}
      {awardDeleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '28px', borderRadius: '12px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginBottom: '12px', fontSize: '20px', color: '#1a1a2e' }}>Delete Award?</h2>
            <p style={{ marginBottom: '24px', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>Are you sure you want to delete <strong style={{ color: '#1a1a2e' }}>{awardDeleteConfirm}</strong>? Both BG and EN entries will be removed.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAwardDeleteConfirm(null)} style={{ padding: '10px 20px', backgroundColor: '#f5f5f5', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
              <button onClick={confirmAwardDelete} style={{ padding: '10px 20px', backgroundColor: '#c33', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '28px', borderRadius: '12px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginBottom: '12px', fontSize: '20px', color: '#1a1a2e' }}>Delete Article?</h2>
            <p style={{ marginBottom: '24px', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>Are you sure you want to delete <strong style={{ color: '#1a1a2e' }}>{deleteConfirm}</strong>?</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{ padding: '10px 20px', backgroundColor: '#f5f5f5', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{ padding: '10px 20px', backgroundColor: '#c33', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const emptyMention: MediaMention = {
  id: '', publisher_bg: '', publisher_en: '', title_bg: '', title_en: '', summary_bg: '', summary_en: '',
  source_url: '', logo_path: '', article_bg_slug: '', article_en_slug: '', sort_order: 0, published: false,
};

function MediaMentionsAdmin({ apiCall, articles }: { apiCall: (action: string, data?: any) => Promise<any>; articles: Article[] }) {
  const [mentions, setMentions] = useState<MediaMention[]>([]);
  const [form, setForm] = useState<MediaMention | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const articlePairs = articles.filter(article => article.language === 'bg').map(bg => ({
    bg, en: articles.find(en => en.language === 'en' && en.created_at === bg.created_at),
  })).filter((pair): pair is { bg: Article; en: Article } => Boolean(pair.en));

  const reload = React.useCallback(async () => {
    try {
      const result = await apiCall('media-list');
      setMentions(result.mentions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load media mentions.');
    }
  }, [apiCall]);

  React.useEffect(() => { reload(); }, [reload]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      await apiCall('media-save', { mention: form });
      setForm(null);
      setSuccess('Media mention saved.');
      await reload();
    } catch (err: any) {
      setError(err.message || 'Failed to save media mention. Publish both linked articles first.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    setBusy(true);
    setError('');
    try {
      await apiCall('media-delete', { id: deleteId });
      setDeleteId(null);
      setSuccess('Media mention removed.');
      await reload();
    } catch (err: any) {
      setError(err.message || 'Failed to remove media mention.');
    } finally {
      setBusy(false);
    }
  };

  const fields: { key: keyof MediaMention; label: string; long?: boolean }[] = [
    { key: 'publisher_bg', label: 'Publisher (BG)' }, { key: 'publisher_en', label: 'Publisher (EN)' },
    { key: 'title_bg', label: 'Headline (BG)' }, { key: 'title_en', label: 'Headline (EN)' },
    { key: 'summary_bg', label: 'Summary (BG)', long: true }, { key: 'summary_en', label: 'Summary (EN)', long: true },
  ];

  return (
    <div className="space-y-5">
      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">{error}</p>}
      {success && <p role="status" className="rounded-lg bg-green-50 p-3 text-green-800">{success}</p>}
      {!form && <button type="button" className="btn-primary" onClick={() => { setForm({ ...emptyMention, sort_order: mentions.length + 1 }); setError(''); }}>+ New media mention</button>}
      {form && <form onSubmit={save} className="space-y-4 rounded-xl bg-white p-6 shadow">
        <h2 className="text-xl font-semibold text-navy">{mentions.some(item => item.id === form.id) ? 'Edit media mention' : 'New media mention'}</h2>
        <label className="block">ID (lowercase letters and hyphens) <input className="input" required pattern="[a-z0-9-]+" value={form.id} disabled={mentions.some(item => item.id === form.id)} onChange={e => setForm({ ...form, id: e.target.value })} /></label>
        <label className="block">Law-firm article pair
          <select className="input" required value={form.article_bg_slug} onChange={e => {
            const pair = articlePairs.find(item => item.bg.slug === e.target.value);
            setForm({ ...form, article_bg_slug: pair?.bg.slug || '', article_en_slug: pair?.en.slug || '' });
          }}>
            <option value="">Select bilingual article</option>
            {articlePairs.map(({ bg, en }) => <option key={bg.slug} value={bg.slug}>{bg.title} ({bg.published && en.published ? 'published' : 'draft'})</option>)}
          </select>
        </label>
        <div className="grid gap-4 md:grid-cols-2">
          {fields.map(({ key, label, long }) => <label key={key} className="block">{label}
            {long ? <textarea className="input min-h-24" required value={String(form[key])} onChange={e => setForm({ ...form, [key]: e.target.value })} /> : <input className="input" required value={String(form[key])} onChange={e => setForm({ ...form, [key]: e.target.value })} />}
          </label>)}
        </div>
        <label className="block">Publisher report URL <input className="input" type="url" required pattern="https://.*" value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })} /></label>
        <label className="block">Publisher logo (local path or uploaded PNG/WebP)
          <input className="input" required value={form.logo_path} onChange={e => setForm({ ...form, logo_path: e.target.value })} placeholder="/assets/images/publisher-logo.svg" />
        </label>
        <label className="block">Upload logo (PNG or WebP)
          <input className="input" type="file" accept="image/png,image/webp" disabled={uploading} onChange={async e => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (!['image/png', 'image/webp'].includes(file.type) || file.size > 2_000_000) { setError('Use a PNG or WebP logo under 2 MB.'); return; }
            setUploading(true);
            try { const path = await uploadAwardImage(file, form.id || 'media-logo'); setForm(current => current && ({ ...current, logo_path: path })); }
            catch (err: any) { setError(err.message || 'Logo upload failed.'); }
            finally { setUploading(false); }
          }} />
        </label>
        {form.logo_path && <img src={form.logo_path} alt="Publisher logo preview" className="h-14 max-w-48 object-contain" />}
        <label className="block">Display order <input className="input" type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: Number(e.target.value) })} /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} /> Published (requires both linked articles published)</label>
        <div className="flex gap-3"><button className="btn-primary" disabled={busy || uploading}>Save mention</button><button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancel</button></div>
      </form>}
      <div className="space-y-3">{mentions.map(mention => <div key={mention.id} className="flex flex-wrap items-center gap-4 rounded-xl bg-white p-4 shadow">
        <img src={mention.logo_path} alt={mention.publisher_bg} className="h-12 w-32 object-contain" />
        <div className="min-w-0 flex-1"><strong className="text-navy">{mention.title_bg}</strong><p className="text-sm text-slate">{mention.published ? 'PUBLISHED' : 'DRAFT'} · Order {mention.sort_order}</p></div>
        <button type="button" onClick={() => { setForm({ ...mention }); setError(''); }} className="text-navy underline">Edit</button>
        <button type="button" onClick={() => setDeleteId(mention.id)} className="text-red-700 underline">Delete</button>
      </div>)}</div>
      {deleteId && <div role="alertdialog" aria-label="Delete media mention" className="rounded-xl border border-red-300 bg-white p-4"><p>Delete this media mention? The linked articles will remain.</p><button disabled={busy} onClick={remove} className="mr-4 text-red-700 underline">Confirm delete</button><button onClick={() => setDeleteId(null)} className="underline">Cancel</button></div>}
    </div>
  );
}

const WorkingAdminPage: React.FC = () => (
  <>
    <Helmet>
      <title>Admin | Адвокат Петър Николов</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <WorkingAdmin />
  </>
);

export default WorkingAdminPage;
